import { useCallback, ClipboardEvent, useMemo, useRef, FocusEvent, useState, memo } from 'react';
import { useMenuTriggerState } from 'react-stately';
import { useFilter } from 'react-aria';
import { useMountEffect, usePreviousState } from '@direct-frontend/stdlib';

import { TreeNode, useCheckboxTreeState } from '../../../../hooks/useTreeState';
import { cn } from '../../../../lib/css/className/className';
import { IconAlert, IconRefresh } from '../../../../icons';
import { ClickableIcon } from '../../../ClickableIcon';
import { List, ListSection, ListStubItem, ListTreeItem } from '../../../List';
import { Popup } from '../../../Popup';
import { Spinner } from '../../../Spinner';
import { HStack } from '../../../Stack';
import { TagTextInput } from '../TagTextInput/TagTextInput';
import { TagPseudoTrigger } from '../TagPseudoTrigger/TagPseudoTrigger';

import { TagTreeEditorProps } from './types';
import './TagTreeEditor@desktop.css';

const emptyFilterFn = () => true;
const cls = cn('TagTreeEditor');

const RenderedTreeItem = memo(function RenderedTreeItem({
    item,
    level,
    isChecked,
    isDisabled,
    isExpanded,
    setChecked,
    setExpanded,
    dataTestId,
    isLoading,
    hasLoadError,
    isIndeterminate,
    hasExpandIcon,
    isExpandSuppressed,
    onRetryLoadChildren,
    onExpandNode,
}: {
    item: TreeNode;
    level: number;
    isChecked: boolean;
    isDisabled?: boolean;
    isExpanded: boolean;
    setChecked: (value: boolean) => void;
    setExpanded: (value: boolean) => void;
    dataTestId: string;
    isLoading: boolean;
    hasLoadError: boolean;
    isIndeterminate: boolean;
    hasExpandIcon: boolean;
    isExpandSuppressed: boolean;
    onRetryLoadChildren?: (nodeId: string) => void;
    onExpandNode?: (nodeId: string, isExpanded: boolean) => void;
}) {
    const onRetryLoad = useCallback(() => {
        onRetryLoadChildren?.(item.id);
    }, [item.id, onRetryLoadChildren]);

    const onExpandChange = useCallback(
        (value: boolean) => {
            setExpanded(value);
            onExpandNode?.(item.id, value);
        },
        [item.id, onExpandNode, setExpanded],
    );

    const stateAddonRight = useMemo(() => {
        if (isLoading) {
            return <Spinner size="24" color="gray" data-testid={`${dataTestId}.Spinner`} />;
        }
        if (hasLoadError) {
            return (
                <HStack gap="4" alignItems="center">
                    <IconAlert
                        size="24"
                        color="current"
                        className={cls('errorIcon')}
                        data-testid={`${dataTestId}.ErrorIcon`}
                    />
                    <ClickableIcon
                        color="current"
                        onClick={onRetryLoadChildren ? onRetryLoad : undefined}
                        data-testid={`${dataTestId}.RetryLoad`}
                    >
                        <IconRefresh size="24" />
                    </ClickableIcon>
                </HStack>
            );
        }
        return undefined;
    }, [dataTestId, hasLoadError, isLoading, onRetryLoad, onRetryLoadChildren]);

    return (
        <ListTreeItem
            data-testid={dataTestId}
            level={level}
            isDisabled={isDisabled}
            isExpanded={isExpanded}
            hasCheckbox={item.isSelectable !== false}
            isSelected={isChecked}
            isIndeterminate={isIndeterminate}
            hasExpandIcon={hasExpandIcon}
            addonRight={stateAddonRight}
            onChange={setChecked}
            onExpandChange={isExpandSuppressed ? undefined : onExpandChange}
        >
            {item.label}
        </ListTreeItem>
    );
});

/**
 * Редактор дерева для TagGroup.
 * Из-за специфики работы дерева управление выбором происходит не через стандартные пропсы TagGroup,
 * а через проп changedNodes у TagTreeEditor. Для получения тегов используется хук useTreeTags
 * @example
 * ```tsx
 * const nodes = [
 *     {
 *         id: '1',
 *         label: 'Node 1',
 *         children: [
 *             {
 *                 id: '1.1',
 *                 label: 'Node 1.1',
 *                 children: [],
 *             },
 *             {
 *                 id: '1.2',
 *                 label: 'Node 1.2',
 *                 children: [],
 *             },
 *         ],
 *     },
 * ];
 * const [changedNodes, setChangedNodes] = useState<ChangedNodes>([]);
 * const tags = useTreeTags({ changedNodes, items: nodes, initialCheckboxValue: false });
 *
 * return <TagGroup tags={tags} tagEditorFactory={props => (
 *      <TagTreeEditor
 *          {...props}
 *          nodes={nodes}
 *          initialCheckboxValue={false}
 *          changedNodes={changedNodes}
 *          setChangedNodes={setChangedNodes}
 *      />
 * )} />;
 * ```
 */
export const TagTreeEditor = function TagTreeEditor(props: TagTreeEditorProps) {
    const {
        nodes,
        initialLevel = 1,
        initialCheckboxValue,
        changedNodes,
        setChangedNodes,
        tagGroupSize,
        onChange,
        onBlur,
        onPaste,
        popupPlacement,
        filter = 'contains',
        childrenLoadingMap,
        childrenErrorMap,
        indeterminateMap,
        onExpandNode,
        onRetryLoadChildren,
        onSearch,
        isSearching,
        forcedExpandedIds,
        'data-testid': dataTestId = 'TagTreeEditor',
        addonBeforeSection: propsAddonBeforeSection,
    } = props;
    const { open, isOpen, close } = useMenuTriggerState({ defaultOpen: false });
    const prevNodes = usePreviousState(nodes);
    const popupRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [filterSuccess, setFilterSuccess] = useState(true);

    const isPopupAllowed = (nodes.length > 0 && filterSuccess) || Boolean(props.emptySection) || Boolean(isSearching);

    const addonBeforeSection = useMemo(
        () =>
            typeof propsAddonBeforeSection === 'function' ? propsAddonBeforeSection(close) : propsAddonBeforeSection,
        [propsAddonBeforeSection, close],
    );

    const { renderTree, setExpanded, collapseAll } = useCheckboxTreeState({
        nodes: nodes,
        initialCheckboxValue,
        changedNodes,
        setChangedNodes,
        forcedExpandedIds,
    });
    const pseudoTriggerRef = useRef<HTMLDivElement>(null);
    const onInputFocus = useCallback(() => {
        open();
    }, [open]);

    const onInputBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            if (event.relatedTarget instanceof HTMLElement && popupRef.current?.contains(event.relatedTarget)) {
                inputRef.current?.focus();
                return;
            }
            onBlur?.(event);
        },
        [onBlur],
    );

    const defaultFilter = useFilter({ sensitivity: 'base' });
    const appliedFilter = (() => {
        if (filter === 'none') {
            return emptyFilterFn;
        }
        if (typeof filter === 'string') {
            return defaultFilter[filter];
        }
        return filter;
    })();

    const onFilter = useCallback(
        (value: string) => {
            if (!nodes || !value) {
                setFilterSuccess(true);
                return;
            }
            collapseAll();

            function expandFoundNodes(node: TreeNode): boolean {
                let found = false;
                if (node.children) {
                    for (const child of node.children) {
                        if (expandFoundNodes(child)) {
                            // Раскрываем родителя
                            setExpanded(node.id, true);

                            found = true;
                        }
                    }
                }

                return found || appliedFilter(node.label, value);
            }

            const found = nodes.map(expandFoundNodes).some(Boolean);
            setFilterSuccess(found);
        },
        [appliedFilter, collapseAll, nodes, setExpanded],
    );

    const onInputChange = useCallback(
        (value: string) => {
            onChange(value);
            if (onSearch) {
                onSearch(value);
                return;
            }
            onFilter(value);
        },
        [onChange, onFilter, onSearch],
    );

    const onInputPaste = useCallback(
        (event: ClipboardEvent<HTMLInputElement>, value?: string) => {
            onPaste?.(event);

            if (value) {
                if (onSearch) {
                    onSearch(value);
                    return;
                }
                onFilter(value);
            }
        },
        [onFilter, onPaste, onSearch],
    );

    useMountEffect(() => {
        if (props.inputValue && !onSearch) {
            onFilter(props.inputValue);
        }
    });

    if (nodes !== prevNodes && props.inputValue && !onSearch) {
        onFilter(props.inputValue);
    }

    const popupContent = (() => {
        if (isSearching) {
            return <ListStubItem width="100%" data-testid={`${dataTestId}.SearchingStub`} />;
        }
        if (!(filterSuccess && nodes.length > 0)) {
            return props.emptySection;
        }
        return (
            <>
                {addonBeforeSection && <ListSection>{addonBeforeSection}</ListSection>}
                <ListSection>
                    {renderTree(
                        ({
                            level,
                            item,
                            isChecked,
                            isDisabled,
                            isExpanded,
                            setChecked,
                            setExpanded: setExpandedBound,
                        }) => {
                            if (item.parent && childrenLoadingMap?.[item.parent.id]) {
                                return null;
                            }

                            if (
                                filter !== 'none' &&
                                !onSearch &&
                                props.inputValue &&
                                !isExpanded &&
                                !appliedFilter(item.label, props.inputValue)
                            ) {
                                return null;
                            }

                            return (
                                <RenderedTreeItem
                                    item={item}
                                    level={level}
                                    isChecked={isChecked}
                                    isDisabled={isDisabled}
                                    isExpanded={isExpanded}
                                    setChecked={setChecked}
                                    setExpanded={setExpandedBound}
                                    dataTestId={`${dataTestId}.${item.id}`}
                                    isLoading={Boolean(childrenLoadingMap?.[item.id])}
                                    hasLoadError={Boolean(childrenErrorMap?.[item.id])}
                                    isIndeterminate={Boolean(indeterminateMap?.[item.id])}
                                    hasExpandIcon={
                                        item.isExpandable !== false &&
                                        (Boolean(item.children?.length) || item.isExpandable === true)
                                    }
                                    isExpandSuppressed={Boolean((props.inputValue && !onSearch) || forcedExpandedIds)}
                                    onRetryLoadChildren={onRetryLoadChildren}
                                    onExpandNode={onExpandNode}
                                />
                            );
                        },
                        initialLevel,
                    )}
                </ListSection>
            </>
        );
    })();

    return (
        <>
            <TagPseudoTrigger ref={pseudoTriggerRef} tagGroupSize={tagGroupSize} />
            <TagTextInput
                {...props}
                ref={inputRef}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                value={props.inputValue}
                onPaste={onInputPaste}
                onChange={onInputChange}
                autoFocus
                data-testid={`${dataTestId}.TextInput`}
            />
            <Popup
                ref={popupRef}
                isOpen={isOpen && isPopupAllowed}
                triggerRef={pseudoTriggerRef}
                placement={popupPlacement}
                matchTriggerWidth
                autoHide={false}
                autoFocusProps={{ autoFocus: false, contain: false }}
                data-testid={`${dataTestId}.Popup`}
            >
                <List size={tagGroupSize ?? 'm'} width="max">
                    {popupContent}
                </List>
            </Popup>
        </>
    );
};

export default TagTreeEditor;
